import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faCircleCheck,
  faBan,
  faTag,
} from "@fortawesome/free-solid-svg-icons";
import { Button } from "../../components/ui";
import { getVoucherById, getVoucherUsages } from "./voucherApi";
import "./Voucher.css";

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-MY", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-MY", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

function VoucherDetailsPage() {
  const navigate = useNavigate();
  const { voucherId } = useParams();
  const [voucher, setVoucher] = useState(null);
  const [usages, setUsages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadDetails = async () => {
      setLoading(true);
      setError("");
      try {
        const [currentVoucher, currentUsages] = await Promise.all([
          getVoucherById(voucherId),
          getVoucherUsages(voucherId),
        ]);
        if (!currentVoucher) {
          setError("Voucher not found.");
          setVoucher(null);
          return;
        }
        setVoucher(currentVoucher);
        setUsages(Array.isArray(currentUsages) ? currentUsages : []);
      } catch (err) {
        setError(
          err.response?.data?.message ||
            err.response?.data ||
            err.message ||
            "Failed to load voucher details.",
        );
      } finally {
        setLoading(false);
      }
    };

    loadDetails();
  }, [voucherId]);

  return (
    <div className="voucher-page">
      <div className="voucher-header">
        <div>
          <h1 className="page-title">Voucher Details</h1>
          <p className="page-subtitle">
            {loading
              ? "Loading voucher details..."
              : voucher
                ? `${voucher.code} · ${usages.length} usage${usages.length === 1 ? "" : "s"}`
                : "Voucher not found"}
          </p>
        </div>
        <Button
          variant="ghost"
          leftIcon={<FontAwesomeIcon icon={faArrowLeft} />}
          onClick={() => navigate("/voucher")}
        >
          Back to Vouchers
        </Button>
      </div>

      {error && (
        <div className="voucher-banner voucher-banner-error">{error}</div>
      )}

      {loading ? (
        <div className="voucher-empty-state">Loading voucher details...</div>
      ) : voucher ? (
        <>
          <div className="voucher-details-card">
            <div className="voucher-details-summary">
              <div className="voucher-details-item">
                <span>Code</span>
                <strong>{voucher.code}</strong>
              </div>
              <div className="voucher-details-item">
                <span>Discount Type</span>
                <span
                  className={`voucher-pill voucher-pill-${voucher.discountType.toLowerCase()}`}
                >
                  {voucher.discountType || "—"}
                </span>
              </div>
              <div className="voucher-details-item">
                <span>Discount Value</span>
                <strong>
                  {voucher.discountType === "percentage"
                    ? `${voucher.discountValue}%`
                    : `$${voucher.discountValue}`}
                </strong>
              </div>
              <div className="voucher-details-item">
                <span>Expiry Date</span>
                <strong>{formatDate(voucher.expiryDate)}</strong>
              </div>
              <div className="voucher-details-item">
                <span>Status</span>
                <span
                  className={`voucher-status ${voucher.active ? "active" : "inactive"}`}
                >
                  <FontAwesomeIcon
                    icon={voucher.active ? faCircleCheck : faBan}
                  />
                  {voucher.active ? "Active" : "Inactive"}
                </span>
              </div>
              <div className="voucher-details-item">
                <span>Total Usages</span>
                <strong>{usages.length}</strong>
              </div>
            </div>
          </div>

          <div className="voucher-table-wrap">
            {usages.length === 0 ? (
              <div className="voucher-empty-state">
                <FontAwesomeIcon icon={faTag} className="voucher-empty-icon" />
                <h3>No usages yet</h3>
                <p>This voucher has not been used by any patient.</p>
              </div>
            ) : (
              <table className="voucher-table">
                <thead>
                  <tr>
                    <th>Patient</th>
                    <th>Patient ID</th>
                    <th>Gender</th>
                    <th>Used At</th>
                    <th>Appointment ID</th>
                  </tr>
                </thead>
                <tbody>
                  {usages.map((usage) => (
                    <tr key={usage.id}>
                      <td>
                        <div className="voucher-code-cell">
                          <span className="voucher-code">
                            {usage.patient?.name || "—"}
                          </span>
                        </div>
                      </td>
                      <td>{usage.patient?.id || "—"}</td>
                      <td>{usage.patient?.gender || "—"}</td>
                      <td>{formatDateTime(usage.usedAt)}</td>
                      <td>{usage.appointmentId ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : (
        <div className="voucher-empty-state">
          {error || "Voucher not found."}
        </div>
      )}
    </div>
  );
}

export default VoucherDetailsPage;
